import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';

import { IconButton, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import classNames from 'classnames';
import TlPagination from '../form-elements/tl-pagination';
import TlForwardBackPagination from '../form-elements/tl-forward-back-pagination';
import useMongoSearch from '../../hooks/useMongoSearch';
import styles from './composer.module.css';
import localStyles from './composer-search-bar.module.css';

export const paginationComponents = {
  tlPagnation: TlPagination,
  tlForwardBack: TlForwardBackPagination,
};

const ComposerSearchBar = ({
  fetchObjects,
  searchArgs,
  serviceArgs,
  searchType,
  refreshTimestamp,
  headerElements,
  emptyState,
  onResultsChanged,
}) => {
  const { search } = useLocation();

  const [objectsLoaded, setObjectsLoaded] = useState(false);

  const params = new URLSearchParams(search);
  const sourceTypes = params.getAll('sourceType');

  const { objects, onSearchChange, onSearchClear, autoCompleteTerms, refreshObjects, loading } = useMongoSearch({
    serviceArgs,
    fetchObjects,
    searchArgs,
    searchType,
    sourceTypes,
  });

  useEffect(() => {
    if (refreshTimestamp) {
      refreshObjects();
    }
  }, [refreshTimestamp]);

  useEffect(() => {
    if (loading === false) {
      setObjectsLoaded(true);
    }
  }, [loading]);

  useEffect(() => {
    if (objectsLoaded) {
      refreshObjects();
    }
  }, [search]);

  useEffect(() => {
    onResultsChanged(objects);
  }, [objects]);

  const initialEmptyState = objectsLoaded && !autoCompleteTerms && objects.length === 0;

  if (objectsLoaded === false) {
    return null;
  }

  return (
    <div>
      <Box className={localStyles.headerContainer}>
        <TextField
          disabled={initialEmptyState}
          // onFocus={onFocus}
          id="autocomplete"
          value={autoCompleteTerms || ''}
          placeholder="Search"
          className={localStyles.textField}
          onChange={onSearchChange}
          variant="outlined"
          autoComplete="off"
          InputProps={{
            style: { fontSize: '13px' },
            startAdornment: (
              <SearchIcon fontSize="small" style={{ paddingRight: 4, paddingTop: 2, color: '#bcbcbc' }} />
            ),
            endAdornment: (
              <IconButton
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSearchClear();
                }}
                disableRipple
                disabled={autoCompleteTerms === ''}
                style={{ padding: 8, marginRight: -8 }}
                size="large"
              >
                {autoCompleteTerms && <HighlightOffIcon fontSize="small" />}
              </IconButton>
            ),
          }}
        />
        {headerElements}
      </Box>
      {!initialEmptyState && !objects.length && (
        <div
          style={{ minHeight: 200 }}
          className={classNames(styles.rounded, styles.border, styles.p4, styles.flex, styles.justifyCenter)}
        >
          <div className={styles.center}>
            <div className={classNames(styles.strong, styles.font16, styles.mb)}>No Results Found</div>
            <div className={styles.light}>{`Your search for '${autoCompleteTerms}' did not return any results.`}</div>
          </div>
        </div>
      )}
      {initialEmptyState && <>{emptyState}</>}
    </div>
  );
};

ComposerSearchBar.defaultProps = {
  searchArgs: {},
  serviceArgs: [],
  refreshTimestamp: null,
  headerElements: null,
  emptyState: null,
};

ComposerSearchBar.propTypes = {
  fetchObjects: PropTypes.func.isRequired,
  searchArgs: PropTypes.shape({}),
  serviceArgs: PropTypes.arrayOf(PropTypes.string),
  searchType: PropTypes.string.isRequired,
  refreshTimestamp: PropTypes.number,
  headerElements: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func]),
  emptyState: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func]),
  onResultsChanged: PropTypes.func.isRequired,
};

export default ComposerSearchBar;
